"use client"

import * as React from 'react'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm, type CrudField, type CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { apiCall } from '@open-mercato/ui/backend/utils/apiCall'
import { raiseCrudError } from '@open-mercato/ui/backend/utils/serverErrors'
import { useT } from '@open-mercato/shared/lib/i18n/context'

type DocumentDetails = {
  id: string
  documentType: 'CMR' | 'ECMR' | 'CIM' | 'SMGS' | 'MULTIMODAL'
  documentNumber: string
  status: 'DRAFT' | 'ISSUED' | 'SIGNED' | 'DELIVERED' | 'ARCHIVED'
  cargoDescription?: string | null
  weightKg?: number | null
  packagesCount?: number | null
}

export default function EditDocumentPage({ params }: { params?: { id?: string } }) {
  const t = useT()
  const id = params?.id
  const [initialValues, setInitialValues] = React.useState<Record<string, unknown> | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    let cancelled = false

    async function loadDocument() {
      if (!id) {
        setError(t('documents.messages.loadError', 'Failed to load documents'))
        setIsLoading(false)
        return
      }

      try {
        const call = await apiCall<DocumentDetails>(`/api/freight/documents/${encodeURIComponent(id)}`)
        if (!call.ok) {
          await raiseCrudError(call.response, t('documents.messages.loadError', 'Failed to load documents'))
        }

        if (cancelled) return

        const item = call.result
        if (!item) {
          setError(t('documents.messages.loadError', 'Failed to load documents'))
          setIsLoading(false)
          return
        }

        setInitialValues({
          documentNumber: item.documentNumber,
          status: item.status,
          cargoDescription: item.cargoDescription ?? '',
          weightKg: item.weightKg ?? null,
          packagesCount: item.packagesCount ?? null,
        })
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error && err.message
            ? err.message
            : t('documents.messages.loadError', 'Failed to load documents')
          setError(message)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    void loadDocument()

    return () => {
      cancelled = true
    }
  }, [id, t])

  const fields = React.useMemo<CrudField[]>(() => [
    {
      id: 'documentNumber',
      label: t('documents.form.fields.documentNumber', 'Document Number'),
      type: 'text',
      required: true,
    },
    {
      id: 'status',
      label: t('documents.form.fields.status', 'Status'),
      type: 'select',
      options: [
        { value: 'DRAFT', label: t('documents.lookups.status.DRAFT', 'Draft') },
        { value: 'ISSUED', label: t('documents.lookups.status.ISSUED', 'Issued') },
      ],
    },
    {
      id: 'cargoDescription',
      label: t('documents.form.fields.cargoDescription', 'Cargo Description'),
      type: 'textarea',
    },
    {
      id: 'weightKg',
      label: t('documents.form.fields.weightKg', 'Weight (kg)'),
      type: 'number',
    },
    {
      id: 'packagesCount',
      label: t('documents.form.fields.packagesCount', 'Packages Count'),
      type: 'number',
    },
  ], [t])

  const groups = React.useMemo<CrudFormGroup[]>(() => [
    {
      id: 'main',
      title: t('documents.pages.list.title', 'Transport Documents'),
      column: 1,
      fields: ['documentNumber', 'status', 'cargoDescription'],
    },
    {
      id: 'metrics',
      title: t('documents.form.fields.notes', 'Details'),
      column: 2,
      fields: ['weightKg', 'packagesCount'],
    },
  ], [t])

  const successRedirect = React.useMemo(
    () => `/backend/documents?flash=${encodeURIComponent(t('documents.messages.updateSuccess', 'Document updated successfully'))}&type=success`,
    [t],
  )

  if (isLoading) {
    return (
      <Page>
        <PageBody>
          <LoadingMessage label={t('documents.messages.loading', 'Loading documents...')} />
        </PageBody>
      </Page>
    )
  }

  if (error || !id) {
    return (
      <Page>
        <PageBody>
          <ErrorMessage label={error ?? t('documents.messages.loadError', 'Failed to load documents')} />
        </PageBody>
      </Page>
    )
  }

  return (
    <Page>
      <PageBody>
        <CrudForm
          title={t('documents.form.title.edit', 'Edit Transport Document')}
          backHref={`/backend/documents/${encodeURIComponent(id)}`}
          fields={fields}
          groups={groups}
          initialValues={initialValues ?? undefined}
          submitLabel={t('documents.actions.save', 'Save')}
          cancelHref={`/backend/documents/${encodeURIComponent(id)}`}
          successRedirect={successRedirect}
          onSubmit={async (values) => {
            const call = await apiCall(`/api/freight/documents/${encodeURIComponent(id)}`, {
              method: 'PATCH',
              body: JSON.stringify(values),
            })
            if (!call.ok) {
              await raiseCrudError(call.response, t('documents.messages.updateError', 'Failed to update document'))
            }
          }}
        />
      </PageBody>
    </Page>
  )
}
